import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';

import { _nextTrackEvent } from '../index';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

export const EVENT_IDS = {
  CLICK_ADD_MF_FEE: '6717',
  CLICK_EDIT_MF_FEE: '6718',
  CLICK_SAVE_FEE: '6719',
  CLICK_CANCEL_FEE_EDIT: '6720',
  CLICK_DELETE_MF_FEE: '6721',
  CONFIRM_DELETE_FEE: '6722',
  CLICK_ADD_DISCLAIMER: '6723',
  CLICK_EDIT_DISCLAIMER: '6725',
  CLICK_SAVE_DISCLAIMER: '6726',
  CLICK_VIEW_FEE_DETAILS: '8232',
} as const;

export const TRIGGER_SOURCES = {
  BUTTON_TO_EDIT_FEE: 'button_to_edit_fee',
  VIEW_DETAILS_BUTTON_TO_EDIT_FEE: 'view_details_button_to_edit_fee',
  BUTTON_TO_VIEW_FEE_DETAILS: 'button_to_view_fee_details',
  LIST_ITEM_VIEW_FEE_DETAILS: 'list_item_view_fee_details',
} as const;

type CostsAndFeesEventProps = {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
};

type CostsAndFeesEventWithTriggerProps = CostsAndFeesEventProps & {
  triggerObject?: string;
};

type EditFeeEventProps = CostsAndFeesEventProps & {
  triggerSource?: typeof TRIGGER_SOURCES.BUTTON_TO_EDIT_FEE | typeof TRIGGER_SOURCES.VIEW_DETAILS_BUTTON_TO_EDIT_FEE;
};

type ViewFeeDetailsEventProps = CostsAndFeesEventProps & {
  triggerSource?: typeof TRIGGER_SOURCES.BUTTON_TO_VIEW_FEE_DETAILS | typeof TRIGGER_SOURCES.LIST_ITEM_VIEW_FEE_DETAILS;
};

// https://dataportal.zillowgroup.net/event-registry/6717
export const emitClickAddFeeEvent = ({
  currentProperty,
  user,
  triggerObject = 'zram_costs_and_fees',
}: CostsAndFeesEventWithTriggerProps) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.CLICK_ADD_MF_FEE,
      event_type_version_id: '2',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'zram_posting_path',
      trigger_source_nm: 'button_to_add_fee',
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'add_fee',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6718
export const emitClickEditFeeEvent = ({
  currentProperty,
  user,
  triggerSource = TRIGGER_SOURCES.BUTTON_TO_EDIT_FEE,
}: EditFeeEventProps) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_EDIT_MF_FEE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: triggerSource,
    },
    semantic: {
      semantic_event_nm: 'edit_fee',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6719
export const emitClickSaveFeeEvent = ({ currentProperty, user }: CostsAndFeesEventProps) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_SAVE_FEE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'button_to_save_fee',
    },
    semantic: {
      semantic_event_nm: 'save_fee',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6720
export const emitClickCancelFeeEditEvent = ({ currentProperty, user }: CostsAndFeesEventProps) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_CANCEL_FEE_EDIT,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'cancel_fee_edit',
    },
    semantic: {
      semantic_event_nm: 'cancel_fee_edit',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6721
export const emitMultifamilyClickDeleteFeeEvent = ({ currentProperty, user }: CostsAndFeesEventProps) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_DELETE_MF_FEE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'button_to_delete_fee',
    },
    semantic: {
      semantic_event_nm: 'delete_fee',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6722
export const emitConfirmDeleteFeeEvent = ({ currentProperty, user }: CostsAndFeesEventProps) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CONFIRM_DELETE_FEE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'button_to_confirm_fee_deletion',
    },
    semantic: {
      semantic_event_nm: 'confirm_delete_fee',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6723
export const emitClickAddDisclaimerEvent = ({ currentProperty, user }: CostsAndFeesEventProps) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_ADD_DISCLAIMER,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'button_to_add_disclaimer',
    },
    semantic: {
      semantic_event_nm: 'add_disclaimer',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6725
export const emitClickEditDisclaimerEvent = ({ currentProperty, user }: CostsAndFeesEventProps) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_EDIT_DISCLAIMER,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'button_to_edit_disclaimer',
    },
    semantic: {
      semantic_event_nm: 'edit_disclaimer',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6726
export const emitClickSaveDisclaimerEvent = ({ currentProperty, user }: CostsAndFeesEventProps) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_SAVE_DISCLAIMER,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'button_to_save_disclaimer',
    },
    semantic: {
      semantic_event_nm: 'save_disclaimer',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8232
export const emitClickViewFeeDetailsEvent = ({
  currentProperty,
  user,
  triggerSource = TRIGGER_SOURCES.LIST_ITEM_VIEW_FEE_DETAILS,
}: ViewFeeDetailsEventProps) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_VIEW_FEE_DETAILS,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: triggerSource,
    },
    semantic: {
      semantic_event_nm: 'view_fee_details',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });
